import React, { useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { Form, Col, Row } from 'react-bootstrap';
import { useFormikContext } from 'formik';

import { Input, Select, TextAreaField } from 'components/Forms';
import { diabetesKeys, getMedicalConditionOptions } from 'constants/constants';
import { usePrefUnitForMeasurementClass } from 'hooks';
import { calcBmi } from 'utils/units';

import { IInvitation } from '../Onboard';

interface IMedicalInformationProps {
  focusHeightRef?: React.MutableRefObject<boolean>;
}

const MedicalInformation = ({ focusHeightRef }: IMedicalInformationProps) => {
  const intl = useIntl();
  const heightRowRef = useRef<HTMLDivElement>(null);
  const { values, setFieldValue, handleChange } = useFormikContext<IInvitation>();

  const handleBmiInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    setFieldValue('bmiChangedFlag', true);
  };

  const allConditions: ISelectOption[] = getMedicalConditionOptions(
    intl,
    values?.patientInfo.gender === 'male' ? '1' : '2'
  );

  const heightUnit = usePrefUnitForMeasurementClass('height');
  const weightUnit = usePrefUnitForMeasurementClass('weight');

  const isImperial = heightUnit === 'ft';

  const bmi = useMemo(
    () =>
      calcBmi({
        height: values.medicalInfo.height,
        heightInches: values.medicalInfo.heightInches,
        weight: values.medicalInfo.weight,
        heightUnit,
        weightUnit
      }),
    [
      values.medicalInfo.height,
      values.medicalInfo.heightInches,
      values.medicalInfo.weight,
      heightUnit,
      weightUnit
    ]
  );

  useEffect(() => {
    if (!focusHeightRef?.current) return;
    focusHeightRef.current = false;
    const timeout = setTimeout(() => {
      const input = heightRowRef.current?.querySelector('input');
      input?.scrollIntoView({ block: 'center' });
      input?.focus();
    }, 0);
    return () => clearTimeout(timeout);
  }, [focusHeightRef]);

  useEffect(() => {
    const { primaryRpmReason, secondaryRpmReason } = values.medicalInfo;
    if (!primaryRpmReason && secondaryRpmReason) {
      setFieldValue('medicalInfo.primaryRpmReason', secondaryRpmReason);
      setFieldValue('medicalInfo.secondaryRpmReason', '');
    }
  }, [values, setFieldValue]);

  return (
    <>
      <h4 className="mb-4" data-test="medical-info-title">
        {intl.formatMessage({ id: 'medicalInformationKey' })}
      </h4>
      <Row ref={heightRowRef} className="mb-3 align-items-end">
        <Form.Group className="form-group" as={Col} md="3">
          <Form.Label>
            {intl.formatMessage({ id: 'heightKey' })}
            {!isImperial && ` (${heightUnit})`}
          </Form.Label>
          {isImperial ? (
            <Row className="g-2">
              <Col>
                <Form.Label className="small text-muted mb-1">ft</Form.Label>
                <Input
                  type="number"
                  name="medicalInfo.height"
                  data-test="invite-medical-info-height-ft"
                  className="no-spinner"
                  min="0"
                  step="1"
                  onChange={handleBmiInputChange}
                />
              </Col>
              <Col>
                <Form.Label className="small text-muted mb-1">in</Form.Label>
                <Input
                  type="number"
                  name="medicalInfo.heightInches"
                  data-test="invite-medical-info-height-in"
                  className="no-spinner"
                  min="0"
                  max="11"
                  step="1"
                  onChange={handleBmiInputChange}
                />
              </Col>
            </Row>
          ) : (
            <Input
              type="number"
              name="medicalInfo.height"
              data-test="invite-medical-info-height"
              className="no-spinner"
              min="0"
              step="0.01"
              onChange={handleBmiInputChange}
            />
          )}
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="3">
          <Form.Label>
            {intl.formatMessage({ id: 'weightKey' })} ({weightUnit})
          </Form.Label>
          <Input
            type="number"
            name="medicalInfo.weight"
            data-test="invite-medical-info-weight"
            className="no-spinner"
            min="0"
            step="0.1"
            onChange={handleBmiInputChange}
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="3">
          <Form.Label>
            {intl.formatMessage({ id: 'dryWeightKey' })} ({weightUnit})
          </Form.Label>
          <Input
            type="number"
            name="medicalInfo.dryWeight"
            data-test="invite-medical-info-dry-weight"
            className="no-spinner"
            min="0"
            step="0.1"
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="3">
          <Form.Label>{intl.formatMessage({ id: 'bmiKey' })}</Form.Label>
          <Form.Control
            data-test="invite-medical-info-bmi"
            value={bmi}
            placeholder={intl.formatMessage({ id: 'autoCalculatedKey' })}
            disabled
            readOnly
          />
        </Form.Group>
      </Row>
      <Row>
        <Form.Group className="form-group" as={Col} md="6">
          <Select
            label={intl.formatMessage({ id: 'primaryConditionKey' })}
            name="medicalInfo.primaryRpmReason"
            options={allConditions.filter(
              (x) =>
                x.value !== values?.medicalInfo?.secondaryRpmReason &&
                !values?.medicalInfo?.conditions
                  ?.map((condition) => condition.value)
                  .includes(x.value) &&
                !(
                  (diabetesKeys.includes(x.value) &&
                    diabetesKeys.includes(values.medicalInfo.secondaryRpmReason || '')) ||
                  (diabetesKeys.includes(x.value) &&
                    values.medicalInfo.conditions
                      ?.map((x) => x.value)
                      .some((i) => diabetesKeys.includes(i)))
                )
            )}
            dataTestId="invite-medical-info-primaryRpmReason"
            isClearable
            wrapLines={true}
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Select
            label={intl.formatMessage({ id: 'secondaryConditionKey' })}
            name="medicalInfo.secondaryRpmReason"
            options={allConditions.filter(
              (x) =>
                x.value !== values?.medicalInfo?.primaryRpmReason &&
                !values?.medicalInfo?.conditions
                  ?.map((condition) => condition.value)
                  .includes(x.value) &&
                !(
                  (diabetesKeys.includes(x.value) &&
                    diabetesKeys.includes(values.medicalInfo.primaryRpmReason || '')) ||
                  (diabetesKeys.includes(x.value) &&
                    values.medicalInfo.conditions
                      ?.map((x) => x.value)
                      .some((i) => diabetesKeys.includes(i)))
                )
            )}
            isDisabled={!values.medicalInfo.primaryRpmReason}
            dataTestId="invite-medical-info-secondaryRpmReason"
            isClearable
            wrapLines={true}
          />
        </Form.Group>
        <Form.Group className="form-group" as={Col} md="6">
          <Select
            label={intl.formatMessage({ id: 'otherConditionsKey' })}
            name="medicalInfo.conditions"
            options={allConditions.filter(
              (x) =>
                x.value !== values?.medicalInfo?.primaryRpmReason &&
                x.value !== values?.medicalInfo?.secondaryRpmReason &&
                !(
                  diabetesKeys.includes(x.value) &&
                  (diabetesKeys.includes(values.medicalInfo.primaryRpmReason || '') ||
                    diabetesKeys.includes(values.medicalInfo.secondaryRpmReason || ''))
                )
            )}
            dataTestId="invite-medical-info-conditions"
            isClearable
            isMulti
            wrapLines={true}
          />
        </Form.Group>
      </Row>
      <hr className="mt-0" />
      <Row>
        <Form.Group className="form-group" as={Col} md="12">
          <TextAreaField
            data-test="message-otherMedicalInfo"
            label={intl.formatMessage({ id: 'otherMedicalInfoKey' })}
            name="medicalInfo.otherMedicalInfo"
            rows={4}
          />
        </Form.Group>
      </Row>
      <hr className="mt-0" />
      <Row>
        <Form.Group className="form-group" as={Col} md="6">
          <Input
            type="payer"
            label={intl.formatMessage({ id: 'coverageKey' })}
            name="medicalInfo.payer"
            data-test="invite-medical-info-payer"
          />
        </Form.Group>
      </Row>
    </>
  );
};

export default MedicalInformation;
